import { useEffect, useState } from "react";
import { getDashboardSnapshot } from "./api";
import { ErrorScreen, LoadingScreen, Shell, routes } from "./components/Shell";
import type { DashboardSnapshotV2 } from "./types";
import { WorkspaceProvider } from "./workspace";
import { HittersView, LeagueView, MatchupView, MyTeamView, PitchersView, TodayView, TransactionsView } from "./views/Views";

const normalizePath = (path: string) => routes.some((route) => route.path === path) ? path : "/today";

function usePath() {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));
  useEffect(() => {
    if (window.location.pathname !== path) window.history.replaceState({}, "", path);
    const onPop = () => setPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [path]);
  const navigate = (nextPath: string) => {
    if (nextPath === path) return;
    window.history.pushState({}, "", nextPath);
    setPath(nextPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return { path, navigate };
}

function CurrentView({ path, snapshot }: { path: string; snapshot: DashboardSnapshotV2 }) {
  switch (path) {
    case "/my-team": return <MyTeamView snapshot={snapshot} />;
    case "/matchup": return <MatchupView snapshot={snapshot} />;
    case "/league": return <LeagueView snapshot={snapshot} />;
    case "/hitters": return <HittersView snapshot={snapshot} />;
    case "/pitchers": return <PitchersView snapshot={snapshot} />;
    case "/transactions": return <TransactionsView snapshot={snapshot} />;
    default: return <TodayView snapshot={snapshot} />;
  }
}

export function App() {
  const { path, navigate } = usePath();
  const [state, setState] = useState<{ snapshot: DashboardSnapshotV2 | null; isFixture: boolean; error: string | null; attempt: number }>({ snapshot: null, isFixture: false, error: null, attempt: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { const timer = window.setInterval(() => tick((value) => value + 1), 60000); const refresh = () => { if (document.visibilityState === "visible") setState((current) => ({ ...current, attempt: current.attempt + 1 })); }; window.addEventListener("focus", refresh); document.addEventListener("visibilitychange", refresh); return () => { clearInterval(timer); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); }; }, []);

  useEffect(() => {
    const controller = new AbortController();
    setRefreshing(true);
    setState((current) => ({ ...current, error: null }));
    getDashboardSnapshot(controller.signal)
      .then(({ data, isFixture }) => { if (!controller.signal.aborted) setState((current) => ({ ...current, snapshot: data, isFixture, error: null })); })
      .catch((error: unknown) => { if (!controller.signal.aborted) setState((current) => ({ ...current, error: error instanceof Error ? error.message : "Unknown loading error" })); })
      .finally(() => { if (!controller.signal.aborted) setRefreshing(false); });
    return () => controller.abort();
  }, [state.attempt]);

  if (state.error && !state.snapshot) return <ErrorScreen error={state.error} retry={() => setState((current) => ({ ...current, attempt: current.attempt + 1 }))} />;
  if (!state.snapshot) return <LoadingScreen />;
  return <WorkspaceProvider snapshot={state.snapshot}><Shell meta={state.snapshot.meta} method={state.snapshot.method} currentPath={path} navigate={navigate} isFixture={state.isFixture}><div className="refresh-toolbar"><span>Playoff Edition · Your daily workspace</span><button disabled={refreshing} onClick={() => setState((current) => ({ ...current, attempt: current.attempt + 1 }))}>{refreshing ? "Checking report…" : "Refresh report"}</button></div>{state.error && <p role="alert" className="refresh-error">The newest report could not load. Showing the last loaded report. {state.error}</p>}<CurrentView path={path} snapshot={state.snapshot} /></Shell></WorkspaceProvider>;
}
